import { useToast } from '@razorpay/blade/components';
import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'hooks/useSearchParams';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import PerquisitesBreakdown from '../components/PerquisitesBreakdown';
import PerquisitesModal from '../components/PerquisitesModal';
import DeleteModal from '../components/generics/DeleteModal';
import { PerquisitesFormData } from '../types';
import createUserPerquisiteAssignments from 'api/gql-utils/perquisitesAssignments/createUserPerquisiteAssignments';
import listPerquisiteComponentAssignments from 'api/gql-utils/perquisitesAssignments/listPerquisiteComponentAssignments';
import { PayrollListPerquisiteComponentAssignmentsResponse } from 'api/gql-utils/perquisitesAssignments/types';
import updateUserPerquisiteAssignments from 'api/gql-utils/perquisitesAssignments/updateUserPerquisiteAssignments';
import deleteUserPerquisiteAssignments from 'api/gql-utils/perquisitesAssignments/deleteUserPerquisiteAssignments';
import useReduxSelector from 'utils/useReduxSelector';
import { generateUserId } from 'utils/commons';
import { paiseAmountToRupee, rupeeAmountToPaise } from 'utils/Numbers';
import useFeature from 'hooks/useFeature';
import listPerquisitesComponents from 'api/gql-utils/perquisitesComponents/listPerquisitesComponents';

const PerquisitesView = () => {
  const [isPerquisiteModalOpen, setIsPerquisiteModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const toast = useToast();
  const queryClient = useQueryClient();
  const { urlParams } = useSearchParams();
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const userId = urlParams.get('userId') || loggedInUserId;
  const { isPerquisiteInstantTDSEnabled } = useFeature('perquisiteInstantTDS');

  // Query for fetching perquisite assignments
  let organizationId = `org_${useReduxSelector(
    (state) => state?.loggedInUser?.data?.currentOrganization?.id || '',
  )}`;
  const { data: perquisitesData, isLoading: isPerquisitesLoading } = useQuery({
    queryKey: [listPerquisiteComponentAssignments, userId],
    queryFn: () =>
      listPerquisiteComponentAssignments({
        input: {
          userIds: [generateUserId(userId || '')],
          merchantId: organizationId,
          tenantId: organizationId,
        },
      }),
    enabled: !!userId,
    select: (data: PayrollListPerquisiteComponentAssignmentsResponse) => {
      return (
        data.payrollListPerquisiteComponentAssignments.data?.map((assignment) => ({
          id: assignment.id,
          component: assignment.componentName,
          amountPaidByEmployee: parseFloat(assignment.amountAlreadyPaid),
          totalValue: parseFloat(assignment.totalAmount),
          componentId: assignment.componentId,
          payrollMonth: assignment.payrollMonth,
        })) || []
      );
    },
  });

  // Behaviour lives on the component, not the assignment. Unfiltered by status: an
  // assignment can point at a deactivated component.
  const { data: behaviourByComponentId = {}, isLoading: isBehaviourLoading } = useQuery({
    queryKey: [listPerquisitesComponents, 'behaviour'],
    queryFn: () => listPerquisitesComponents({}),
    enabled: isPerquisiteInstantTDSEnabled,
    select: (data) =>
      Object.fromEntries(
        (data?.payrollListPerquisiteComponents?.components ?? []).map((component) => [
          component.id,
          component.taxDeductionBehaviour,
        ]),
      ),
  });

  const perquisitesRows = useMemo(
    () =>
      (perquisitesData || []).map((row) => ({
        ...row,
        taxDeductionBehaviour: behaviourByComponentId[row.componentId],
      })),
    [perquisitesData, behaviourByComponentId],
  );

  // Mutation for creating perquisite assignments
  const createPerquisiteMutation = useMutation({
    mutationFn: createUserPerquisiteAssignments,
    onSuccess: (response) => {
      if (response.payrollCreateUserPerquisiteAssignments.success) {
        toast.show({
          color: 'positive',
          content: 'Perquisite added successfully',
          duration: 2000,
        });
        // Invalidate and refetch perquisite assignments
        queryClient.invalidateQueries({ queryKey: [listPerquisiteComponentAssignments, userId] });
        setIsPerquisiteModalOpen(false);
        setSelectedComponentId(null);
        initialFormData = {
          componentId: '',
          amountPaidByEmployee: 0,
          totalValue: 0,
        };
      } else {
        toast.show({
          color: 'negative',
          content:
            response.payrollCreateUserPerquisiteAssignments.message || 'Failed to add perquisite',
        });
      }
    },
    onError: (error) => {
      toast.show({
        color: 'negative',
        content: 'Failed to add perquisite. Please try again.',
      });
    },
  });

  const updatePerquisiteMutation = useMutation({
    mutationFn: updateUserPerquisiteAssignments,
    onSuccess: (response) => {
      if (response.payrollUpdateUserPerquisiteAssignments.success) {
        toast.show({
          color: 'positive',
          content: 'Perquisite updated successfully',
          duration: 2000,
        });
      }
      queryClient.invalidateQueries({ queryKey: [listPerquisiteComponentAssignments, userId] });
      setIsPerquisiteModalOpen(false);
      setSelectedComponentId(null);
      initialFormData = {
        componentId: '',
        amountPaidByEmployee: 0,
        totalValue: 0,
      };
    },
    onError: (error) => {
      toast.show({
        color: 'negative',
        content: 'Failed to update perquisite. Please try again.',
      });
    },
  });

  const deletePerquisiteMutation = useMutation({
    mutationFn: deleteUserPerquisiteAssignments,
    onSuccess: (response) => {
      if (response.payrollDeleteUserPerquisiteAssignments.success) {
        toast.show({
          color: 'positive',
          content: 'Perquisite deleted successfully',
          duration: 2000,
        });
        queryClient.invalidateQueries({ queryKey: [listPerquisiteComponentAssignments, userId] });
        setIsDeleteModalOpen(false);
        setSelectedComponentId(null);
      }
    },
    onError: (error) => {
      toast.show({
        color: 'negative',
        content: 'Failed to delete perquisite. Please try again.',
      });
    },
  });

  const onDismiss = () => {
    setIsPerquisiteModalOpen(false);
    setSelectedComponentId(null);
  };

  const onDismissDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedComponentId(null);
  };

  const onDelete = () => {
    deletePerquisiteMutation.mutate({
      ids: [selectedComponentId || ''],
    });
  };
  // Determine the initial form data based on the selected component
  let initialFormData: PerquisitesFormData = {
    componentId: '',
    amountPaidByEmployee: 0,
    totalValue: 0,
  };
  if (selectedComponentId) {
    // Find the perquisite by ID
    const selectedPerquisite = perquisitesData?.find((item) => item.id === selectedComponentId);
    // If found, use its data
    if (selectedPerquisite) {
      initialFormData = {
        componentId: selectedPerquisite.componentId,
        amountPaidByEmployee: paiseAmountToRupee(selectedPerquisite.amountPaidByEmployee),
        totalValue: paiseAmountToRupee(selectedPerquisite.totalValue),
        payrollMonth: selectedPerquisite.payrollMonth,
      };
    } else {
      initialFormData.componentId = selectedComponentId;
    }
  }
  // Find the name of the component for the delete modal
  const selectedPerquisiteName = useMemo(
    () => perquisitesData?.find((p) => p.id === selectedComponentId)?.component,
    [perquisitesData, selectedComponentId],
  );

  const onSubmit = (formData: PerquisitesFormData) => {
    // Transform form data to API format
    const assignmentData = {
      userComponent: [
        {
          userId: String(userId),
          componentId: formData.componentId!,
          totalAmount: (formData.totalValue ? formData.totalValue * 100 : 0)?.toString() || '0',
          amountAlreadyPaid:
            (formData.amountPaidByEmployee ? formData.amountPaidByEmployee * 100 : 0)?.toString() ||
            '0',
          includeInCompensation: true,
          includeInPayroll: true,
          ...(formData.payrollMonth ? { payrollMonth: formData.payrollMonth } : {}),
        },
      ],
    };

    if (selectedComponentId) {
      const storedMonth = perquisitesData?.find((p) => p.id === selectedComponentId)?.payrollMonth;
      updatePerquisiteMutation.mutate({
        userComponent: [
          {
            id: selectedComponentId,
            totalAmount:
              (formData.totalValue ? rupeeAmountToPaise(formData.totalValue) : 0)?.toString() ||
              '0',
            amountAlreadyPaid:
              (formData.amountPaidByEmployee
                ? rupeeAmountToPaise(formData.amountPaidByEmployee)
                : 0
              )?.toString() || '0',
            includeInCompensation: true,
            includeInPayroll: true,
            ...(!storedMonth && formData.payrollMonth
              ? { payrollMonth: formData.payrollMonth }
              : {}),
          },
        ],
      });
    } else {
      createPerquisiteMutation.mutate(assignmentData);
    }
  };

  return (
    <>
      <PerquisitesBreakdown
        setSelectedComponentId={setSelectedComponentId}
        openPerquisiteModal={() => setIsPerquisiteModalOpen(true)}
        openDeleteModal={() => setIsDeleteModalOpen(true)}
        perquisitesData={perquisitesRows}
        isLoading={isPerquisitesLoading || isBehaviourLoading}
        showTdsDeduction={isPerquisiteInstantTDSEnabled}
      />
      <PerquisitesModal
        key={selectedComponentId || 'new'}
        initialFormData={initialFormData}
        isOpen={isPerquisiteModalOpen}
        onDismiss={onDismiss}
        onSubmit={onSubmit}
        perquisitesAssignments={perquisitesData || []}
        isCTALoading={
          selectedComponentId
            ? updatePerquisiteMutation.isLoading
            : createPerquisiteMutation.isLoading
        }
      />
      <DeleteModal
        componentName="Perquisite"
        description={`You are about to delete the perquisite '${selectedPerquisiteName}'`}
        descriptionOnAction="Please note any past payslip and salary register will not be effective only future salary register and employee payslip will change"
        isOpen={isDeleteModalOpen}
        onDismiss={onDismissDeleteModal}
        onSubmit={onDelete}
      />
    </>
  );
};

export default PerquisitesView;
